"use client";

const TOKEN_KEY = "sms_admin_token";
const USER_KEY = "sms_admin_user";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setSession(token: string, user: unknown) {
  window.localStorage.setItem(TOKEN_KEY, token);
  window.localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function getStoredUser<T = unknown>(): T | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(USER_KEY);
  return raw ? (JSON.parse(raw) as T) : null;
}

export function clearSession() {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(USER_KEY);
}

// Single logout: ask the gateway to revoke this account's session (invalidating its tokens across
// every connected app — the hub, airtime, …), then clear the local session. Network/401 errors are
// ignored since we're signing out regardless.
export async function logoutEverywhere(): Promise<void> {
  try {
    await api("auth/logout", { method: "POST", timeoutMs: 5000 });
  } catch {
    /* already invalid or offline — clear locally anyway */
  }
  clearSession();
}

interface ApiOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;      // attach bearer token (default true)
  timeoutMs?: number;  // abort after this long (default 30s) so a hung request never freezes a page
}

// Calls the SMS gateway via the local /api/gateway proxy.
export async function api<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = "GET", body, auth = true, timeoutMs = 30000 } = options;
  const headers: Record<string, string> = {};

  if (auth) {
    const token = getToken();
    if (token) headers["authorization"] = `Bearer ${token}`;
  }
  if (body !== undefined) headers["content-type"] = "application/json";

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res: Response;
  try {
    res = await fetch(`/api/gateway/${path.replace(/^\//, "")}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal
    });
  } catch (e) {
    clearTimeout(timer);
    if ((e as Error).name === "AbortError") throw new ApiError("Request timed out. Please try again.", 0);
    throw new ApiError((e as Error).message || "Network error.", 0);
  }
  clearTimeout(timer);

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { error: text };
  }

  if (!res.ok) {
    // Session expired or invalid token: drop the stale session and bounce to login.
    // Only for authenticated calls — the login request itself (auth: false) must
    // surface its own 401 ("invalid username or password") instead of redirecting.
    if (res.status === 401 && auth) {
      clearSession();
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }

    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(message, res.status);
  }

  return data as T;
}
